<script setup>
/**
 * "Start from a description" (spec P-8.2), beside the three starters: she writes a paragraph or
 * records a voice note, presses "Draft task · {credits}", confirms the price, and the composer opens
 * the draft on its Check screen, switched off. Nothing is saved here and nothing is saved by the
 * server: the draft lives in the composer (and its kept local draft) until she saves it.
 *
 * Inside the composer, so the confirm is drawn IN PLACE (`AiConfirmInline`): the composer imports
 * no overlay.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmInline from '../AiConfirmInline.vue';
import VoiceNoteButton from '../VoiceNoteButton.vue';
import WasloTextArea from '../WasloTextArea.vue';
import { useAiConfirm } from '../../composables/useAiConfirm.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { useWasloI18n } from '../../i18n.js';
import { wasloApi, withConfirm } from '../../api/client.js';
import { describeChannels } from '../../helpers/setup-ai.js';

const props = defineProps({
  /** The account's channels (`api/channels.js`), in the composer's words. */
  channels: { type: Array, default: () => [] },
});

const emit = defineEmits(['described']);

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();
const price = useAiConfirm({ accountId, op: 'describe_task' });

const LIMIT = 2000;
const text = ref('');
const busy = ref(false);
const failure = ref('');

const draftLabel = computed(() =>
  busy.value
    ? t('WASLO.SETUP_AI.DESCRIBE.DRAFTING')
    : price.label(t('WASLO.SETUP_AI.DESCRIBE.DRAFT'))
);

const addSpoken = said => {
  text.value = [text.value.trim(), said]
    .filter(Boolean)
    .join(' ')
    .slice(0, LIMIT);
};

const draft = async () => {
  const description = text.value.trim();
  if (description === '' || busy.value || accountId.value === null) return;
  busy.value = true;
  failure.value = '';
  const channels = describeChannels(props.channels);
  try {
    const outcome = await price.confirmAndRun({
      run: credits =>
        wasloApi.describeTask(
          accountId.value,
          {
            description,
            lang: String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en',
            channels,
            // A form can only be sent on WhatsApp; whether this number may carry one is checked
            // when the form is published, so a form button drafted here is words until then.
            formsEligible: channels.includes('whatsapp'),
          },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    emit('described', outcome.result);
  } catch (error) {
    if (error?.status === 422 && error?.fields?.description) {
      failure.value = error.fields.description;
    } else if (error?.status === 422) {
      failure.value = t('WASLO.SETUP_AI.DESCRIBE.UNUSABLE');
    } else {
      failure.value = error?.message || t('WASLO.SETUP_AI.DESCRIBE.FAILED');
    }
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
    data-waslo-describe-task
  >
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t('WASLO.SETUP_AI.DESCRIBE.TITLE') }}
    </p>
    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('WASLO.SETUP_AI.DESCRIBE.HINT') }}
    </p>
    <WasloTextArea
      v-model="text"
      :placeholder="t('WASLO.SETUP_AI.DESCRIBE.PLACEHOLDER')"
      :max-length="LIMIT"
      :show-count="false"
      min-height="5rem"
      :disabled="busy"
      data-waslo-describe-text
    />
    <VoiceNoteButton :disabled="busy" @text="addSpoken" />
    <div class="flex">
      <Button
        :label="draftLabel"
        :disabled="busy || text.trim() === ''"
        data-waslo-describe-draft
        @click="draft"
      />
    </div>
    <AiConfirmInline :controller="price" />
    <p
      v-if="failure"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-describe-failure
    >
      {{ failure }}
    </p>
  </div>
</template>
